/**
 * RBAC — single source of truth for the persona framework in the brief (§4).
 * Enforced in three places with the same table:
 *   1. Worker API (authoritative)
 *   2. Bridge daemon (local privileged actions)
 *   3. UI (hide/show — never trusted as the only gate)
 */

export type GlobalRole = 'GROUP_CEO' | 'GROUP_CFO' | 'IT_ADMIN';

export type PropertyRole =
  | 'PROPERTY_MANAGER'
  | 'FRONT_DESK'
  | 'CASHIER'
  | 'STORE_MANAGER'
  | 'MAINTENANCE_MANAGER'
  | 'KITCHEN_MANAGER'
  | 'GYM_ADMIN'
  | 'FARM_MANAGER'
  | 'FARM_SALES'
  | 'SECURITY_OFFICER'
  | 'HOUSEKEEPING'
  | 'AUDITOR';

export type Role = GlobalRole | PropertyRole;

export type Permission =
  // executive
  | 'exec.dashboard' | 'exec.financials' | 'exec.audit'
  // cctv
  | 'cctv.view.all' | 'cctv.view.assigned' | 'cctv.ptz' | 'cctv.config'
  // hospitality
  | 'hotel.reservations' | 'hotel.checkin' | 'hotel.folio.post' | 'hotel.housekeeping'
  // retail / gym
  | 'pos.sell' | 'pos.shift.close' | 'pos.inventory' | 'pos.discount'
  | 'gym.members.manage' | 'gym.checkin.monitor'
  // inventory & procurement
  | 'inventory.view' | 'inventory.manage' | 'inventory.procure' | 'inventory.approve'
  | 'inventory.receive' | 'inventory.transfer' | 'inventory.waste' | 'inventory.invoice.match'
  // maintenance
  | 'maintenance.view' | 'maintenance.work_orders.manage' | 'maintenance.schedule.manage'
  | 'maintenance.room_status.manage'
  // kitchen / food service
  | 'kitchen.view' | 'kitchen.orders.manage' | 'kitchen.menu.manage' | 'kitchen.recipes.manage'
  | 'kitchen.haccp.manage' | 'kitchen.folio.post'
  // farm
  | 'farm.harvest' | 'farm.b2b.quote' | 'farm.b2b.invoice'
  // hr & finance
  | 'hr.employees' | 'hr.timecards' | 'payroll.run' | 'expense.submit' | 'expense.approve'
  // admin
  | 'admin.properties' | 'admin.cctv.config' | 'admin.api_keys' | 'admin.sync' | 'admin.users'
  // bridge / hardware
  | 'bridge.sync' | 'bridge.isapi.provision' | 'bridge.peripherals';

const ALL_CCTV = ['cctv.view.all', 'cctv.ptz', 'cctv.config'] as const;

export const ROLE_PERMISSIONS: Record<Role, readonly Permission[]> = {
  // --- enterprise leadership -------------------------------------------------
  GROUP_CEO: [
    'exec.dashboard', 'exec.financials', 'exec.audit',
    ...ALL_CCTV,
    'hotel.reservations', 'hotel.checkin', 'hotel.folio.post', 'hotel.housekeeping',
    'pos.sell', 'pos.shift.close', 'pos.inventory', 'pos.discount',
    'gym.members.manage', 'gym.checkin.monitor',
    'inventory.view', 'inventory.manage', 'inventory.procure', 'inventory.approve', 'inventory.receive', 'inventory.transfer', 'inventory.waste', 'inventory.invoice.match',
    'maintenance.view', 'maintenance.work_orders.manage', 'maintenance.schedule.manage', 'maintenance.room_status.manage',
    'kitchen.view', 'kitchen.orders.manage', 'kitchen.menu.manage', 'kitchen.recipes.manage', 'kitchen.haccp.manage', 'kitchen.folio.post',
    'farm.harvest', 'farm.b2b.quote', 'farm.b2b.invoice',
    'hr.employees', 'hr.timecards', 'payroll.run', 'expense.submit', 'expense.approve',
    'admin.properties', 'admin.cctv.config', 'admin.api_keys', 'admin.sync', 'admin.users',
    'bridge.sync', 'bridge.isapi.provision', 'bridge.peripherals',
  ],
  GROUP_CFO: [
    'exec.dashboard', 'exec.financials', 'exec.audit',
    'cctv.view.all',
    'hr.employees', 'hr.timecards', 'payroll.run', 'expense.approve',
    'hotel.folio.post', 'pos.shift.close', 'inventory.view',
  ],
  IT_ADMIN: [
    'exec.dashboard', 'exec.audit',
    ...ALL_CCTV,
    'admin.properties', 'admin.cctv.config', 'admin.api_keys', 'admin.sync', 'admin.users',
    'bridge.sync', 'bridge.isapi.provision', 'bridge.peripherals',
  ],

  // --- property operations ---------------------------------------------------
  PROPERTY_MANAGER: [
    'exec.dashboard', 'cctv.view.assigned', 'cctv.ptz',
    'hotel.reservations', 'hotel.checkin', 'hotel.folio.post', 'hotel.housekeeping',
    'pos.sell', 'pos.shift.close', 'pos.inventory', 'pos.discount',
    'gym.members.manage', 'gym.checkin.monitor',
    'inventory.view', 'inventory.manage', 'inventory.procure', 'inventory.approve', 'inventory.receive', 'inventory.transfer', 'inventory.waste', 'inventory.invoice.match',
    'maintenance.view', 'maintenance.work_orders.manage', 'maintenance.schedule.manage', 'maintenance.room_status.manage',
    'kitchen.view', 'kitchen.orders.manage', 'kitchen.menu.manage', 'kitchen.recipes.manage', 'kitchen.haccp.manage', 'kitchen.folio.post',
    'farm.harvest', 'farm.b2b.quote', 'farm.b2b.invoice',
    'hr.employees', 'hr.timecards', 'expense.submit', 'expense.approve',
    'bridge.sync', 'bridge.peripherals',
  ],
  FRONT_DESK: [
    'hotel.reservations', 'hotel.checkin', 'hotel.folio.post',
    'pos.sell', 'bridge.peripherals',
  ],
  CASHIER: ['pos.sell', 'pos.shift.close', 'bridge.peripherals'],
  STORE_MANAGER: ['pos.sell', 'pos.shift.close', 'pos.inventory', 'pos.discount', 'inventory.view', 'inventory.manage', 'inventory.procure', 'inventory.approve', 'inventory.receive', 'inventory.transfer', 'inventory.waste', 'inventory.invoice.match', 'expense.submit'],
  MAINTENANCE_MANAGER: ['maintenance.view', 'maintenance.work_orders.manage', 'maintenance.schedule.manage', 'maintenance.room_status.manage'],
  KITCHEN_MANAGER: ['kitchen.view', 'kitchen.orders.manage', 'kitchen.menu.manage', 'kitchen.recipes.manage', 'kitchen.haccp.manage', 'kitchen.folio.post'],
  GYM_ADMIN: ['gym.members.manage', 'gym.checkin.monitor', 'pos.sell', 'expense.submit'],
  FARM_MANAGER: ['farm.harvest', 'farm.b2b.quote', 'farm.b2b.invoice', 'cctv.view.assigned', 'expense.submit'],
  FARM_SALES: ['farm.b2b.quote', 'farm.b2b.invoice', 'bridge.peripherals'],
  SECURITY_OFFICER: ['cctv.view.assigned', 'cctv.ptz'],
  HOUSEKEEPING: ['hotel.housekeeping'],
  AUDITOR: ['exec.financials', 'exec.audit', 'cctv.view.assigned', 'inventory.view', 'maintenance.view', 'kitchen.view'],
};

export const isGlobalRole = (r: string): r is GlobalRole =>
  r === 'GROUP_CEO' || r === 'GROUP_CFO' || r === 'IT_ADMIN';

export interface AuthContext {
  userId: string;
  orgId: string;
  globalRole: GlobalRole | null;
  propertyRoles: Array<{ siteId: string; role: PropertyRole; scope?: Record<string, unknown> }>;
}

/** Flatten a principal into the permission set it holds for a given site. */
export function permissionsFor(auth: AuthContext, siteId?: string): Set<Permission> {
  const out = new Set<Permission>();
  if (auth.globalRole) {
    for (const p of ROLE_PERMISSIONS[auth.globalRole]) out.add(p);
  }
  for (const pr of auth.propertyRoles) {
    if (siteId && pr.siteId !== siteId) continue;
    for (const p of ROLE_PERMISSIONS[pr.role] ?? []) out.add(p);
  }
  return out;
}

export function can(auth: AuthContext, permission: Permission, siteId?: string): boolean {
  return permissionsFor(auth, siteId).has(permission);
}

/**
 * Sector gate: a Hotel manager must not open the Farm module even if their
 * role union happens to be broad. Global roles bypass.
 */
export function canAccessModule(auth: AuthContext, module: string, siteKinds: string[]): boolean {
  if (auth.globalRole) return true;
  const map: Record<string, string[]> = {
    hospitality: ['HOTEL', 'MIXED'],
    retail: ['HOTEL', 'MALL', 'SUPERMARKET', 'MIXED', 'LOUNGE'],
    gym: ['GYM', 'MIXED'],
    farm: ['FARM', 'MIXED'],
    inventory: ['HOTEL', 'MALL', 'SUPERMARKET', 'GYM', 'LOUNGE', 'MIXED'],
    maintenance: ['HOTEL', 'MALL', 'SUPERMARKET', 'GYM', 'LOUNGE', 'MIXED'],
    kitchen: ['HOTEL', 'LOUNGE', 'MIXED'],
  };
  const allowed = map[module];
  if (!allowed) return true;
  return siteKinds.some((k) => allowed.includes(k));
}
